import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import BoltIcon from "@mui/icons-material/Bolt";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import { useTranslationApp } from "../../lib/i18next";
import {
  CAMPAIGN_LEARNINGS_TITLE,
  CAMPAIGN_LEARNINGS_SUBTITLE,
  CAMPAIGN_LEARNINGS_KEY_LEARNINGS,
  CAMPAIGN_LEARNINGS_WHAT_WORKED,
  CAMPAIGN_LEARNINGS_WHAT_DIDNT_WORK,
} from "../../i18n/keysTranslations";

const PlatformSection = ({ platform, data, isPositive }) => {
  const { t } = useTranslationApp();

  if (!data || !data.tags?.length) return null;

  return (
    <Box sx={{ mb: 2, "&:last-child": { mb: 0 }, width: "100%" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          mb: 1,
          flexWrap: "wrap",
        }}
      >
        <Chip
          label={t(platform)}
          size="small"
          variant="outlined"
          sx={{
            fontWeight: "bold",
            height: 24,
            borderColor: "divider",
          }}
        />
      </Box>

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
        {data.tags.map((tag, idx) => {
          const label = typeof tag === "object" ? tag.label : tag;
          const description = typeof tag === "object" ? tag.description : null;

          return (
            <Box
              key={idx}
              sx={{
                display: "flex",
                flexDirection: "column",
                border: "1px dashed",
                borderColor: isPositive ? "success.light" : "error.light",
                borderRadius: 1,
                p: 0.75,
                minWidth: 120,
                maxWidth: "100%",
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontWeight: "bold",
                  color: "text.primary",
                  mb: description ? 0.25 : 0,
                }}
              >
                {label}
              </Typography>
              {description && (
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: "0.7rem",
                    color: "text.secondary",
                    lineHeight: 1.3,
                  }}
                >
                  {description}
                </Typography>
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

const CampaignLearnings = ({
  learnings = [],
  layout = "vertical",
  hideHeader = false,
}) => {
  const { t } = useTranslationApp();

  const isHorizontal = layout === "horizontal";

  const renderKeyLearnings = () => {
    if (!learnings.some((l) => l.topLearnings?.length > 0)) return null;

    return (
      <Box
        data-pdf-card
        sx={{
          backgroundColor: "rgba(255, 152, 0, 0.05)",
          border: "1px solid rgba(255, 152, 0, 0.1)",
          borderRadius: 3,
          p: 2,
          height: isHorizontal ? "100%" : "auto",
          display: "flex",
          flexDirection: "column",
          flex: isHorizontal
            ? {
                xs: "1 1 100%",
                md: "1 1 100%",
                lg: "1 1 0%",
              }
            : "none",
          boxSizing: "border-box",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
          <BoltIcon sx={{ color: "#f57c00" }} />
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: "bold", color: "#e65100" }}
          >
            {t(CAMPAIGN_LEARNINGS_KEY_LEARNINGS)}
          </Typography>
        </Box>

        {learnings.map(
          (platformData, pIdx) =>
            platformData.topLearnings?.length > 0 && (
              <Box
                key={pIdx}
                sx={{ mb: pIdx === learnings.length - 1 ? 0 : 2 }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: "bold",
                    color: "#e65100",
                    textTransform: "uppercase",
                    display: "block",
                    mb: 1,
                    letterSpacing: 0.5,
                  }}
                >
                  {t(platformData.platform)}
                </Typography>
                <Box
                  sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}
                >
                  {platformData.topLearnings.map((learning, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: "flex",
                        gap: 1.5,
                        alignItems: "flex-start",
                      }}
                    >
                      <Box
                        sx={{
                          minWidth: 20,
                          height: 20,
                          borderRadius: "50%",
                          backgroundColor: "rgba(255, 152, 0, 0.1)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          mt: 0.2,
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: "bold",
                            color: "#e65100",
                          }}
                        >
                          {index + 1}
                        </Typography>
                      </Box>
                      <Typography
                        variant="body2"
                        sx={{
                          color: "text.primary",
                          lineHeight: 1.4,
                          wordBreak: "break-word",
                        }}
                      >
                        {learning}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            )
        )}
      </Box>
    );
  };

  const renderWorked = () => (
    <Box
      sx={{
        border: "2px solid rgba(76, 175, 80, 0.2)",
        borderRadius: 4,
        p: 1.5,
        backgroundColor: "rgba(76, 175, 80, 0.02)",
        height: isHorizontal ? "100%" : "auto",
        display: "flex",
        flexDirection: "column",
        flex: isHorizontal
          ? {
              xs: "1 1 100%",
              md: "1 1 calc(50% - 8px)",
              lg: "1 1 0%",
            }
          : "none",
        boxSizing: "border-box",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
        <CheckCircleOutlineIcon
          sx={{ color: "success.main", fontSize: "1.2rem" }}
        />
        <Typography
          variant="subtitle1"
          sx={{ fontWeight: "bold", color: "success.dark" }}
        >
          {t(CAMPAIGN_LEARNINGS_WHAT_WORKED)}
        </Typography>
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1,
          backgroundColor: "white",
          borderRadius: 3,
          p: 1.5,
          boxShadow: "0 2px 8px rgba(0,0,0,0.02)",
          width: "100%",
          boxSizing: "border-box",
          flex: isHorizontal ? 1 : "none",
        }}
      >
        {learnings.map((platformData, idx) => (
          <PlatformSection
            key={idx}
            platform={platformData.platform}
            data={platformData.worked}
            isPositive={true}
            t={t}
          />
        ))}
      </Box>
    </Box>
  );

  const renderNotWorked = () => (
    <Box
      sx={{
        border: "2px solid rgba(244, 67, 54, 0.2)",
        borderRadius: 4,
        p: 1.5,
        backgroundColor: "rgba(244, 67, 54, 0.02)",
        height: isHorizontal ? "100%" : "auto",
        display: "flex",
        flexDirection: "column",
        flex: isHorizontal
          ? {
              xs: "1 1 100%",
              md: "1 1 calc(50% - 8px)",
              lg: "1 1 0%",
            }
          : "none",
        boxSizing: "border-box",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
        <HighlightOffIcon sx={{ color: "error.main", fontSize: "1.2rem" }} />
        <Typography
          variant="subtitle1"
          sx={{ fontWeight: "bold", color: "error.dark" }}
        >
          {t(CAMPAIGN_LEARNINGS_WHAT_DIDNT_WORK)}
        </Typography>
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1,
          backgroundColor: "white",
          borderRadius: 3,
          p: 1.5,
          boxShadow: "0 2px 8px rgba(0,0,0,0.02)",
          width: "100%",
          boxSizing: "border-box",
          flex: isHorizontal ? 1 : "none",
        }}
      >
        {learnings.map((platformData, idx) => (
          <PlatformSection
            key={idx}
            platform={platformData.platform}
            data={platformData.notWorked}
            isPositive={false}
            t={t}
          />
        ))}
      </Box>
    </Box>
  );

  return (
    <Box
      data-pdf-learnings
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 2,
        boxSizing: "border-box",
        overflow: "hidden",
      }}
    >
      {/* Header */}
      {!hideHeader && (
        <Box sx={{ px: 0.5 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 0.5 }}>
            {t(CAMPAIGN_LEARNINGS_TITLE)}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t(CAMPAIGN_LEARNINGS_SUBTITLE)}
          </Typography>
        </Box>
      )}

      {/* Content */}
      <Box
        data-pdf-learnings-row
        sx={{
          display: "flex",
          flexDirection: isHorizontal ? "row" : "column",
          flexWrap: isHorizontal ? "wrap" : "nowrap",
          gap: 2,
          alignItems: isHorizontal ? "stretch" : "flex-start",
          width: "100%",
        }}
      >
        {renderKeyLearnings()}
        {renderWorked()}
        {renderNotWorked()}
      </Box>
    </Box>
  );
};

export default CampaignLearnings;
